import { CommonRegistry } from '../components/components';
import {
  CommonFilteredComponentSystem,
  FilteredComponentSystemUpdateEntityArgs,
} from './SystemBase';

/**
 * Applies transient knockback forces to entity velocity and decays them.
 *
 * This system should run AFTER MovementIntentSystem but BEFORE MovementSystem.
 * It adds the knockback impulse to the calculated movement velocity.
 */
export class KnockbackSystem extends CommonFilteredComponentSystem {
  public componentClasses: (keyof CommonRegistry)[] = ['Velocity', 'Knockback'];
  protected simulationBound = true;

  updateEntity({
    world,
    entityId,
    deltaTime,
  }: FilteredComponentSystemUpdateEntityArgs<CommonRegistry>): void {
    const vel = world.getComponent(entityId, 'Velocity')!;
    const kb = world.getComponent(entityId, 'Knockback')!;

    // 1. Apply knockback to current velocity
    // This stacks with any movement intent from MovementIntentSystem
    this.updateComponent(entityId, vel, {
      dx: vel.dx + kb.dx,
      dy: vel.dy + kb.dy,
    });

    // 2. Decay knockback force
    // Using an exponential decay: force = initial * (decay ^ time)
    // A decay of 0.001 per second means it loses 99.9% of its force in 1s.
    const decayFactor = 0.001;
    const friction = Math.pow(decayFactor, deltaTime);

    const nextDx = kb.dx * friction;
    const nextDy = kb.dy * friction;

    // 3. Remove component if force is negligible.
    // The threshold is in pixels per frame. At 60FPS, 2px/frame = 120px/s.
    // This provides a clean cutoff once the recoil has visually finished.
    if (Math.abs(nextDx) < 2 && Math.abs(nextDy) < 2) {
      this.removeComponent(entityId, 'Knockback');
      if (world.hasComponent(entityId, 'FastMovement')) {
        this.removeComponent(entityId, 'FastMovement');
      }
    } else {
      this.updateComponent(entityId, kb, {
        dx: nextDx,
        dy: nextDy,
      });
    }
  }
}
